import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/Field';
import { ShowOnceSecret } from '@/components/domain/ShowOnceSecret';
import { restoreDevice, revokeDevice, rmaDevice } from '@/api/endpoints/devices';
import { queryKeys } from '@/api/queryKeys';
import { errorMessage } from '@/api/errors';
import type { DeviceAdmin, DeviceUnclaimed } from '@/types/api';

export type DeviceAction = 'revoke' | 'rma' | 'restore';

interface Copy {
  title: string;
  body: string;
  /** Typing the serial number is the friction that stops a stray click. Only the
   *  irreversible actions ask for it. */
  requiresTypedSerial: boolean;
  confirmLabel: string;
  danger: boolean;
  success: string;
}

function copyFor(action: DeviceAction, device: DeviceAdmin): Copy {
  if (action === 'revoke') {
    return {
      title: 'Revoke this device?',
      body:
        'Everything it sends afterwards is rejected, so a stolen unit cannot raise a false emergency. ' +
        'An administrator can restore it later, which issues a new claim code.',
      requiresTypedSerial: true,
      confirmLabel: 'Revoke device',
      danger: true,
      success: 'Device revoked. It can no longer report telemetry.',
    };
  }
  if (action === 'rma') {
    return {
      title: 'Mark this device as returned?',
      body:
        'RMA is terminal. The ownership binding closes and the device can never be claimed again. ' +
        'Its crash history is kept — those records are evidence and are never deleted.',
      requiresTypedSerial: true,
      confirmLabel: 'Mark as RMA',
      danger: true,
      success: 'Device marked RMA.',
    };
  }
  // Restore covers two different situations that happen to share an endpoint.
  return device.state === 'locked'
    ? {
        title: 'Release the claim lockout?',
        body:
          'This device locked itself after ten failed claim attempts. Releasing the lockout lets the ' +
          'owner try again with the code printed on their card — that code keeps working and is not changed.',
        requiresTypedSerial: false,
        confirmLabel: 'Release lockout',
        danger: false,
        success: 'Lockout released. The printed claim code works again.',
      }
    : {
        title: 'Restore this device?',
        body:
          'It returns to the unclaimed pool and can be claimed by an owner again. Because it was revoked, ' +
          'a new claim code is issued and the one printed on its housing stops working. You will see the ' +
          'new code once, on the next screen.',
        requiresTypedSerial: false,
        confirmLabel: 'Restore device',
        danger: false,
        success: 'Device restored to the unclaimed pool.',
      };
}

interface DeviceActionDialogProps {
  action: DeviceAction;
  device: DeviceAdmin;
  onClose: () => void;
}

/**
 * One dialog for every administrator lifecycle action, because they differ only in
 * their copy and in how much friction they deserve. Revoke and RMA are hard to walk
 * back, so they demand the serial number typed out; restore is recoverable and
 * needs only a confirmation.
 *
 * A restore that rotates the claim code hands it back exactly once, so that branch
 * refuses to be dismissed until it is acknowledged — the same treatment unclaim
 * gets, for the same reason.
 */
export function DeviceActionDialog({ action, device, onClose }: DeviceActionDialogProps) {
  const queryClient = useQueryClient();
  const [confirmation, setConfirmation] = useState('');
  const [rotated, setRotated] = useState<DeviceUnclaimed | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const copy = copyFor(action, device);

  const mutation = useMutation({
    mutationFn: () => {
      const uid = device.device_uid as string;
      if (action === 'revoke') return revokeDevice(uid).then(() => null);
      if (action === 'rma') return rmaDevice(uid).then(() => null);
      return restoreDevice(uid);
    },
    onSuccess: async (result) => {
      // The 'devices' prefix covers the admin registry, the status poll and every
      // per-device detail query in one call.
      await queryClient.invalidateQueries({ queryKey: queryKeys.devices });
      toast.success(copy.success);
      if (result && result.new_claim_code) {
        setRotated(result);
        return;
      }
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (rotated && rotated.new_claim_code) {
    return (
      <Dialog
        open
        dismissible={false}
        title="Device restored"
        description="A new claim code has been issued. The code printed on the original card no longer works."
        footer={
          <Button variant="primary" disabled={!acknowledged} onClick={onClose}>
            Done
          </Button>
        }
      >
        <div className="space-y-4">
          <ShowOnceSecret
            rows={[
              { label: 'Device ID', value: rotated.device_uid },
              { label: 'New claim code', value: rotated.new_claim_code, isClaimCode: true },
            ]}
            filename={`autoaware-${device.serial_number}-claim-code`}
          />

          <label className="flex items-start gap-2 text-[13px] text-ink-secondary">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
              className="mt-0.5 size-4 rounded border-line-strong text-accent focus:ring-accent-ring"
            />
            I have saved the new claim code.
          </label>
        </div>
      </Dialog>
    );
  }

  const matches =
    !copy.requiresTypedSerial ||
    confirmation.trim().toUpperCase() === device.serial_number.toUpperCase();

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={copy.title}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={copy.danger ? 'danger' : 'primary'}
            disabled={!matches}
            loading={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {copy.confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-2 text-[13px] text-ink-secondary">
          <p>
            <span className="font-medium text-ink">{device.serial_number}</span>
            {device.owner_name ? ` — owned by ${device.owner_name}` : ' — no current owner'}
          </p>
          <p>{copy.body}</p>
        </div>

        {copy.requiresTypedSerial && (
          <TextField
            label={`Type ${device.serial_number} to confirm`}
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        )}
      </div>
    </Dialog>
  );
}
